import { Wallet } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import useTranslations from "@/shared/lang/useTranslations";
import { formatCurrencyAmount } from "@/shared/utils/currencyMinorUnits";

import { SUGGESTION_LABEL_KEYS } from "../../constants/payoutBatches.constants";
import type { PayoutBatchSuggestion } from "../../types/payoutBatches.types";

export interface InsufficientBalanceSuggestionsProps {
  suggestions: PayoutBatchSuggestion[];
  sourceCurrency: string;
  onApply: (itemIds: string[]) => void;
  isApplying: boolean;
}

/**
 * Three ways to fit the selection into the balance, computed by the backend; applying one replaces the
 * selection.
 */
export function InsufficientBalanceSuggestions({
  suggestions,
  sourceCurrency,
  onApply,
  isApplying,
}: InsufficientBalanceSuggestionsProps) {
  const { t, activeLanguage } = useTranslations("payoutBatches");
  if (suggestions.length === 0) return null;

  return (
    <div className="space-y-3">
      <ColocaAlert
        variant="warning"
        icon={Wallet}
        heading={t("suggestions_title")}
        text={t("suggestions_description")}
      />
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {suggestions.map((suggestion) => (
          <ColocaSurface key={suggestion.strategy} padding="md" className="flex flex-col gap-3">
            <p className="text-sm font-semibold text-foreground">
              {t(SUGGESTION_LABEL_KEYS[suggestion.strategy] ?? "suggestion_keep_file_order")}
            </p>
            <p className="font-mono text-xs text-muted-foreground tabular-nums">
              {t("suggestion_meta", {
                count: suggestion.count,
                debit: `${formatCurrencyAmount(suggestion.total_debit, sourceCurrency, activeLanguage)} ${sourceCurrency}`,
                remaining: `${formatCurrencyAmount(suggestion.remaining_balance, sourceCurrency, activeLanguage)} ${sourceCurrency}`,
              })}
            </p>
            <ColocaButton
              variant="default"
              size="sm"
              className="mt-auto self-start"
              loading={isApplying}
              disabled={suggestion.count === 0}
              onClick={() => onApply(suggestion.item_ids)}
            >
              {t("suggestion_apply")}
            </ColocaButton>
          </ColocaSurface>
        ))}
      </div>
    </div>
  );
}
